import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Zap, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const TopBar = () => {
  const { currentUser, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Knowledge', path: '/knowledge' },
    { label: 'Academy', path: '/academy' },
    { label: 'Community', path: '/community' },
    { label: 'Invest', path: '/invest' },
    { label: 'Ask AI', path: '/ask' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleColors = {
    PRACTITIONER: 'hsl(160 81% 17%)',
    INVESTOR: 'hsl(195 54% 35%)',
    GOVERNMENT: 'hsl(310 22% 55%)'
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b backdrop-blur-sm" style={{ 
      backgroundColor: 'hsla(var(--surface), 0.95)',
      borderColor: 'hsl(var(--line))'
    }}>
      <div className="wrap">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: 'hsl(var(--ink))' }}>
              <div className="flex items-center justify-center w-8 h-8 rounded" style={{ backgroundColor: 'hsl(var(--primary))' }}>
                <Zap className="w-4 h-4" style={{ color: 'hsl(var(--primary-foreground))' }} fill="currentColor" />
              </div>
              GridCommons
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="px-3 py-2 text-sm font-medium rounded-lg"
                  style={{
                    color: isActive(item.path) ? 'hsl(var(--primary))' : 'hsl(var(--ink-2))',
                    backgroundColor: isActive(item.path) ? 'hsl(var(--primary-soft))' : 'transparent'
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Role Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="hidden md:flex">
                  {currentUser?.role === 'PRACTITIONER' && 'Practitioner'}
                  {currentUser?.role === 'INVESTOR' && 'Investor'}
                  {currentUser?.role === 'GOVERNMENT' && 'Government'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => switchRole('PRACTITIONER')}>
                  Practitioner
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => switchRole('INVESTOR')}>
                  Investor
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => switchRole('GOVERNMENT')}>
                  Government
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold cursor-pointer"
                  style={{ 
                    backgroundColor: roleColors[currentUser?.role] || 'hsl(var(--primary))',
                    color: 'white'
                  }}
                >
                  {getInitials(currentUser?.name || 'User')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">{currentUser?.name}</span>
                    <span className="text-xs font-normal" style={{ color: 'hsl(var(--ink-muted))' }}>
                      {currentUser?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/me')}>
                  My Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
